import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useStyles } from './style'
import { useSelector } from 'react-redux'
import { authorSlice } from '../../reducers'
import {
  useEvent,
  useEventsOnViewLoad
} from '@emerald-works/react-event-bus-client'
import { authorList } from '../../reducers/author/event-bus'
import AuthorCard, { AUTHOR_CARD_WIDTH } from './author-card'
import { AuthorsLoading } from './authors-loading'

// The cardsContainer gap in style.js.
const CARD_GAP = 16

const AuthorsCarousel = ({ excludeAuthor = '', loading = false }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [currentIndex, setCurrentIndex] = useState(0)
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const carouselRef = useRef(null)
  const [viewport, setViewport] = useState(null)
  const [viewportWidth, setViewportWidth] = useState(0)

  const authors = useSelector(authorSlice.selectors.selectAuthors)
  const authorsLoaded = useSelector(
    authorSlice.selectors.selectAuthorsLoaded
  )
  const [getAuthors] = useEvent([authorList])

  useEventsOnViewLoad(() => {
    if (!authorsLoaded) {
      getAuthors.trigger()
    }
  }, [authors, authorsLoaded])

  const activeAuthors = useMemo(() => {
    if (!excludeAuthor) {
      return authors
    }

    return authors.filter(author => {
      return author.slug !== excludeAuthor
    })
  }, [authors, excludeAuthor])

  // The visible width changes with the page, so it's measured rather than
  // assumed.
  useEffect(() => {
    if (!viewport) return
    const observer = new window.ResizeObserver(([entry]) => setViewportWidth(entry.contentRect.width))
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [viewport])

  const cardWidth = isMobile ? AUTHOR_CARD_WIDTH.mobile : AUTHOR_CARD_WIDTH.desktop

  // Each step moves one card plus the gap between cards. The last step stops
  // where the last card meets the right edge, rather than running past it
  // into empty space or stopping short of it.
  const moveDistance = cardWidth + CARD_GAP
  const trackWidth = activeAuthors.length * moveDistance - CARD_GAP
  const maxOffset = Math.max(0, trackWidth - viewportWidth)
  const maxIndex = Math.ceil(maxOffset / moveDistance)
  const offset = Math.min(currentIndex * moveDistance, maxOffset)

  useEffect(() => {
    setCurrentIndex(0)
  }, [activeAuthors.length])

  const handlePrevious = useCallback(() => {
    // After a resize the index can sit past the last step, so step back from
    // where the carousel actually is.
    setCurrentIndex(prev => Math.max(0, Math.min(prev, maxIndex) - 1))
  }, [maxIndex])

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1))
  }, [maxIndex])

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        handleNext()
      } else if (event.key === 'Home') {
        event.preventDefault()
        setCurrentIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        setCurrentIndex(maxIndex)
      }
    },
    [handlePrevious, handleNext, maxIndex]
  )

  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }, [])

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }, [])

  const showNavigation = maxOffset > 0
  const showLeftArrow = showNavigation && currentIndex > 0
  const showRightArrow = showNavigation && currentIndex < maxIndex

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe && showRightArrow) {
      handleNext()
    }
    if (isRightSwipe && showLeftArrow) {
      handlePrevious()
    }
  }, [touchStart, touchEnd, showLeftArrow, showRightArrow, handleNext, handlePrevious])

  if (loading || getAuthors.isWorking) {
    return <AuthorsLoading />
  }

  if (!authors || authors.length === 0) {
    return (
      <Box className={`${classes.carouselContainer}`}>
        <Typography
          variant='body2'
          color='textSecondary'
          data-test='no-authors'
          textAlign='center'
          sx={{ padding: 2 }}
        >
          No authors available
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      ref={carouselRef}
      className={`${classes.carouselContainer}`}
      role='region'
      aria-label='Authors carousel'
      data-test='authors-carousel'
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
    >
      {showLeftArrow && (
        <IconButton
          onClick={handlePrevious}
          className={`${classes.carouselButton} ${classes.carouselButtonPrevious}`}
          aria-label='Previous authors'
          data-test='authors-carousel-previous-button'
        >
          <ChevronLeft />
        </IconButton>
      )}

      <Box ref={setViewport} className={classes.carouselContent}>
        <Box
          className={classes.carouselSlides}
          sx={{
            transform: `translateX(-${offset}px)`
          }}
        >
          <Box className={classes.cardsContainer}>
            {activeAuthors.map((author, index) => (
              <AuthorCard index={index} key={author.authorId} author={author} width={cardWidth} />
            ))}
          </Box>
        </Box>
      </Box>

      {showRightArrow && (
        <IconButton
          onClick={handleNext}
          className={`${classes.carouselButton} ${classes.carouselButtonNext}`}
          aria-label='Next authors'
          data-test='authors-carousel-next-button'
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  )
}

export default AuthorsCarousel
